import {
  Activity,
  ClipboardPlus,
  FileText,
  Home,
  RotateCcw,
  Stethoscope,
  Syringe,
  FilePlus2
} from "lucide-react";
import { icons } from "lucide-react";
import { CustomIcons } from "@/components/icons/CustomIcons";
import { cn } from "@/lib/utils";

type IconTone = "primary" | "white" | "current";

type CanonicalEventIconType =
  | "consultation"
  | "vaccine"
  | "exam"
  | "prescription"
  | "grooming"
  | "term"
  | "surgery"
  | "return"
  | "application"
  | "stay"
  | "other";

type IconOptions = {
  sizeClass?: string;
  className?: string;
  tone?: IconTone;
};

export type AppointmentIconOption = {
  slug: string;
  labelKey: string;
};

export const APPOINTMENT_ICON_OPTIONS: AppointmentIconOption[] = [
  { slug: "clipboard-plus", labelKey: "settings:appointmentTypes.icons.general" },
  { slug: "stethoscope", labelKey: "settings:appointmentTypes.icons.consultation" },
  { slug: "heart-pulse", labelKey: "settings:appointmentTypes.icons.checkup" },
  { slug: "syringe", labelKey: "settings:appointmentTypes.icons.vaccine" },
  { slug: "pill", labelKey: "settings:appointmentTypes.icons.medication" },
  { slug: "activity", labelKey: "settings:appointmentTypes.icons.surgery" },
  { slug: "briefcase-medical", labelKey: "settings:appointmentTypes.icons.medicalKit" },
  { slug: "shield-plus", labelKey: "settings:appointmentTypes.icons.prevention" },
  { slug: "thermometer", labelKey: "settings:appointmentTypes.icons.temperature" },
  { slug: "bandage", labelKey: "settings:appointmentTypes.icons.dressing" },
  { slug: "microscope", labelKey: "settings:appointmentTypes.icons.lab" },
  { slug: "test-tube", labelKey: "settings:appointmentTypes.icons.exam" },
  { slug: "flask-conical", labelKey: "settings:appointmentTypes.icons.analysis" },
  { slug: "eye", labelKey: "settings:appointmentTypes.icons.eye" },
  { slug: "bone", labelKey: "settings:appointmentTypes.icons.orthopedics" },
  { slug: "scissors", labelKey: "settings:appointmentTypes.icons.grooming" },
  { slug: "hair-dryer", labelKey: "settings:appointmentTypes.icons.bathGrooming" },
  { slug: "bath", labelKey: "settings:appointmentTypes.icons.bath" },
  { slug: "sparkles", labelKey: "settings:appointmentTypes.icons.spa" },
  { slug: "paw-print", labelKey: "settings:appointmentTypes.icons.pet" },
  { slug: "dog", labelKey: "settings:appointmentTypes.icons.dog" },
  { slug: "cat", labelKey: "settings:appointmentTypes.icons.cat" },
  { slug: "baby", labelKey: "settings:appointmentTypes.icons.puppy" },
  { slug: "weight", labelKey: "settings:appointmentTypes.icons.weight" },
  { slug: "utensils", labelKey: "settings:appointmentTypes.icons.nutrition" },
  { slug: "dumbbell", labelKey: "settings:appointmentTypes.icons.physiotherapy" },
  { slug: "rotate-ccw", labelKey: "settings:appointmentTypes.icons.return" },
  { slug: "calendar-clock", labelKey: "settings:appointmentTypes.icons.scheduled" },
  { slug: "clock", labelKey: "settings:appointmentTypes.icons.time" },
  { slug: "alarm-clock", labelKey: "settings:appointmentTypes.icons.urgent" },
  { slug: "ambulance", labelKey: "settings:appointmentTypes.icons.emergency" },
  { slug: "hospital", labelKey: "settings:appointmentTypes.icons.hospital" },
  { slug: "bed-double", labelKey: "settings:appointmentTypes.icons.boarding" },
  { slug: "house", labelKey: "settings:appointmentTypes.icons.daycare" },
  { slug: "car", labelKey: "settings:appointmentTypes.icons.transport" },
  { slug: "truck", labelKey: "settings:appointmentTypes.icons.delivery" },
  { slug: "package", labelKey: "settings:appointmentTypes.icons.package" },
  { slug: "receipt", labelKey: "settings:appointmentTypes.icons.billing" },
  { slug: "credit-card", labelKey: "settings:appointmentTypes.icons.payment" },
  { slug: "circle-help", labelKey: "settings:appointmentTypes.icons.other" },
];

const APPOINTMENT_TYPE_TO_ICON: Record<string, CanonicalEventIconType> = {
  consulta: "consultation",
  cirurgia: "surgery",
  banho_tosa: "grooming",
  retorno: "return",
  vacina: "vaccine",
  aplicacao: "application",
  outro: "other",
  padrao: "consultation",
};

const PATIENT_EVENT_TYPE_TO_ICON: Record<string, CanonicalEventIconType> = {
  consultation: "consultation",
  vaccine: "vaccine",
  exam: "exam",
  prescription: "prescription",
  grooming: "grooming",
  term: "term",
  stay: "stay",
};

const resolveToneClass = (tone: IconTone) => {
  if (tone === "white") return "text-white";
  if (tone === "current") return "text-current";
  return "text-primary";
};

const renderCanonicalIcon = (
  type: CanonicalEventIconType,
  { sizeClass = "h-4 w-4", className, tone = "primary" }: IconOptions = {},
) => {
  if (type === "grooming") {
    const color = tone === "white" ? "white" : tone === "current" ? "currentColor" : "#ef643e";
    return (
      <CustomIcons
        name="hair-dryer"
        strokeWidth={0.1}
        stroke={color}
        fill={color}
        className={cn(sizeClass, resolveToneClass(tone), className)}
      />
    );
  }

  const iconClassName = cn(sizeClass, resolveToneClass(tone), className);
  switch (type) {
    case "consultation":
      return <Stethoscope className={iconClassName} aria-hidden="true" />;
    case "vaccine":
      return <Syringe className={iconClassName} aria-hidden="true" />;
    case "exam":
      return <FileText className={iconClassName} aria-hidden="true" />;
    case "prescription":
      return <FilePlus2 className={iconClassName} aria-hidden="true" />;
    case "term":
      return <FileText className={iconClassName} aria-hidden="true" />;
    case "surgery":
      return <Activity className={iconClassName} aria-hidden="true" />;
    case "return":
      return <RotateCcw className={iconClassName} aria-hidden="true" />;
    case "application":
      return <Syringe className={iconClassName} aria-hidden="true" />;
    case "stay":
      return <Home className={iconClassName} aria-hidden="true" />;
    case "other":
    default:
      return <ClipboardPlus className={iconClassName} aria-hidden="true" />;
  }
};

export const getAppointmentTypeIcon = (type: string, options?: IconOptions) => {
  const canonicalType = APPOINTMENT_TYPE_TO_ICON[type] ?? "other";
  return renderCanonicalIcon(canonicalType, options);
};

export const getPatientEventTypeIcon = (type: string, options?: IconOptions) => {
  const canonicalType = PATIENT_EVENT_TYPE_TO_ICON[type] ?? "other";
  return renderCanonicalIcon(canonicalType, options);
};

export const getTimelineTypeIcon = (type: string, options?: IconOptions) => {
  const canonicalType = PATIENT_EVENT_TYPE_TO_ICON[type] ?? "other";
  return renderCanonicalIcon(canonicalType, options);
};

export const renderIconBySlug = (
  slug: string,
  options: IconOptions = {},
): React.ReactNode => {
  const { sizeClass = "h-4 w-4", className, tone = "primary" } = options;
  const iconClass = cn(sizeClass, resolveToneClass(tone), className);

  if (slug === "hair-dryer") {
    const color = tone === "white" ? "white" : tone === "current" ? "currentColor" : "#ef643e";
    return (
      <CustomIcons
        name="hair-dryer"
        strokeWidth={0.1}
        stroke={color}
        fill={color}
        className={cn(sizeClass, resolveToneClass(tone), className)}
      />
    );
  }

  const componentName = slug
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");

  const IconComponent = icons[componentName as keyof typeof icons];
  if (IconComponent) {
    return <IconComponent className={iconClass} aria-hidden="true" />;
  }
  return <ClipboardPlus className={iconClass} aria-hidden="true" />;
};
